import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { FaChevronDown, FaChevronUp, FaRunning } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const stripHtml = (html = "") => {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
};

const hasHtmlTags = (value = "") => /<\/?[a-z][\s\S]*>/i.test(value);

const normalizeHtmlContent = (value = "") => {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) return "";

  // Si le contenu vient de ReactQuill, on garde les balises HTML.
  if (hasHtmlTags(cleanValue)) {
    return cleanValue;
  }

  // Si c'est du texte simple avec retours à la ligne, on les transforme en <br />.
  return cleanValue.replace(/\n/g, "<br />");
};

const getFallbackGradient = (index = 0) => {
  const gradients = [
    "from-orange-500 via-amber-500 to-yellow-400",
    "from-blue-600 via-sky-500 to-cyan-400",
    "from-purple-600 via-fuchsia-500 to-pink-400",
    "from-emerald-600 via-teal-500 to-cyan-400",
    "from-rose-600 via-red-500 to-orange-400",
    "from-indigo-600 via-blue-500 to-sky-400",
  ];

  return gradients[index % gradients.length];
};

export default function DisciplineCard({
  title = "Discipline",
  ageRange = "",
  imageSrc = "",
  textDiscipline = "",
  index = 0,
}) {
  const [expanded, setExpanded] = useState(false);

  const imageUrl = getImageUrl(imageSrc);
  const fallbackGradient = getFallbackGradient(index);

  const plainText = useMemo(() => {
    return stripHtml(textDiscipline || ageRange || "");
  }, [textDiscipline, ageRange]);

  const contentHtml = useMemo(() => {
    const source =
      textDiscipline ||
      ageRange ||
      "Découvrez cette activité proposée par le club, son ambiance, son fonctionnement et les informations utiles pour débuter.";

    return DOMPurify.sanitize(normalizeHtmlContent(source));
  }, [textDiscipline, ageRange]);

  const cleanAgeRange = useMemo(() => {
    return stripHtml(ageRange || "");
  }, [ageRange]);

  const needsToggle = plainText.length > 220;

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div className="relative h-60 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div
            className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${fallbackGradient}`}
          >
            <FaRunning className="text-6xl text-white/90 drop-shadow-xl" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/30 opacity-0 transition duration-500 group-hover:opacity-100" />

        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="font-barlow text-2xl font-bold leading-tight text-white drop-shadow-lg">
            {title}
          </h3>

          {cleanAgeRange && (
            <p className="mt-2 line-clamp-2 text-sm font-medium leading-6 text-white/75">
              {cleanAgeRange}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Activité encadrée
          </span>
        </div>

        <div className="relative">
          <div
            className={[
              "discipline-content text-sm leading-7 text-slate-600 dark:text-slate-300",
              !expanded && needsToggle ? "max-h-[11rem] overflow-hidden" : "",
            ].join(" ")}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          {!expanded && needsToggle && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent dark:from-slate-900" />
          )}
        </div>

        {needsToggle && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="mt-4 inline-flex items-center gap-2 self-start text-sm font-bold text-primary transition hover:gap-3"
          >
            {expanded ? (
              <>
                Réduire
                <FaChevronUp className="text-xs" />
              </>
            ) : (
              <>
                Lire plus
                <FaChevronDown className="text-xs" />
              </>
            )}
          </button>
        )}
      </div>

      <style>
        {`
          .discipline-content p {
            margin-bottom: 0.75rem;
          }

          .discipline-content p:last-child {
            margin-bottom: 0;
          }

          .discipline-content strong,
          .discipline-content b {
            font-weight: 700;
            color: inherit;
          }

          .discipline-content em {
            font-style: italic;
          }

          .discipline-content ul,
          .discipline-content ol {
            margin: 0.75rem 0;
            padding-left: 1.25rem;
          }

          .discipline-content ul {
            list-style-type: disc;
          }

          .discipline-content ol {
            list-style-type: decimal;
          }

          .discipline-content li {
            margin-bottom: 0.35rem;
          }

          .discipline-content a {
            color: var(--color-primary, #f27131);
            font-weight: 700;
            text-decoration: underline;
            text-underline-offset: 3px;
          }

          .discipline-content h1,
          .discipline-content h2,
          .discipline-content h3 {
            margin: 1rem 0 0.5rem;
            font-weight: 700;
            line-height: 1.2;
            color: inherit;
          }

          .discipline-content h1 {
            font-size: 1.35rem;
          }

          .discipline-content h2 {
            font-size: 1.2rem;
          }

          .discipline-content h3 {
            font-size: 1.05rem;
          }

          .discipline-content br {
            display: block;
            content: "";
            margin-top: 0.35rem;
          }
        `}
      </style>
    </article>
  );
}
